/**
 * DAFCommentary — async-loads the LLM-generated DAF report and renders it
 * with a friendly skeleton/loading state. Cache is server-side keyed by
 * (license, competitor, month_bucket) so a re-open within the same in-game
 * month is instant + free.
 */
import { useEffect, useState } from 'react';
import { Sparkles, RefreshCw, AlertCircle } from 'lucide-react';
import useLicenseStore from '../../store/licenseStore';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DAFCommentary({ competitor, analysis, askingPrice }) {
  const { t } = useTranslation();
  const licenseKey = useLicenseStore(s => s.licenseKey);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const treasury = useGameStore(s => s.treasury || 0);
  const monthlyRevenue = useGameStore(s => s.monthlyRevenue || 0);
  const reputation = useGameStore(s => s.reputation || 50);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const monthBucket = Math.floor(totalDays / 30);

  const fetchReport = async () => {
    // 17/06/2026 — Bug user : avis stratégique DAF restait bloqué sur le
    // skeleton "Le DAF étudie le dossier…". Cause racine : si l'une des
    // gardes ci-dessous échouait (ex. licenseKey vide en démo, analysis
    // pas encore chargé, etc.), on `return` sans `setLoading(false)`, ce
    // qui laissait le skeleton à l'infini. On résout en posant un message
    // fallback explicite avant de quitter.
    if (!BACKEND || !analysis || analysis.locked || !competitor?.id) {
      setLoading(false);
      setError(null);
      setReport(null);
      return;
    }
    if (!licenseKey) {
      setLoading(false);
      setError(t('daf_default_error') || 'Avis indisponible sans licence active.');
      return;
    }
    setLoading(true);
    setError(null);
    // 17/06/2026 — Timeout 25 s pour éviter un skeleton infini si le LLM
    // ou le réseau pédalent. AbortController garantit l'abandon du fetch.
    const _ctrl = new AbortController();
    const _timeout = setTimeout(() => _ctrl.abort(), 25000);
    try {
      const res = await fetch(`${BACKEND}/api/competitor-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: _ctrl.signal,
        body: JSON.stringify({
          license_key: licenseKey,
          competitor_id: competitor.id,
          competitor_name: competitor.name,
          month_bucket: monthBucket,
          ca_monthly: analysis.ca || 0,
          fleet: analysis.fleet || 0,
          drivers: analysis.drivers || 0,
          reputation: analysis.reputation || 0,
          equity: analysis.equity || 0,
          net_margin: analysis.netMargin || 0,
          net_debt: analysis.netDebt || 0,
          health_score: analysis.healthScore || 0,
          weakness_label: analysis.weakness?.label || t('daf_default_no_weakness'),
          recommendation_tier: analysis.recommendation?.tier || 'fair',
          asking_price: askingPrice || 0,
          player_treasury: treasury,
          player_monthly_revenue: monthlyRevenue,
          player_reputation: reputation,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detailMsg = typeof errData.detail === 'string' ? errData.detail : `Error ${res.status}`;
        throw new Error(detailMsg);
      }
      const data = await res.json();
      const reportText = (data.report || '').trim();
      if (!reportText) {
        // 17/06/2026 — Garde-fou : si le backend renvoie un report vide,
        // on traite comme une erreur pour éviter un placeholder muet.
        setError(t('daf_default_error') || 'Le DAF n\'a pas pu rédiger d\'avis (réponse vide).'); /* i18n-keep */
        setReport(null);
      } else {
        setReport(reportText);
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        setError('Le DAF est trop lent à répondre (>25 s). Cliquez sur Réessayer.'); /* i18n-keep */
      } else {
        setError(e.message || t('daf_default_error'));
      }
    } finally {
      clearTimeout(_timeout);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitor?.id, monthBucket]);

  return (
    <div className="p-4 rounded-lg bg-gradient-to-br from-purple-950/30 to-blue-950/30 border border-purple-500/40 space-y-2" data-testid="daf-commentary">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-300" />
          <h3 className="text-xs uppercase tracking-wider text-purple-200 font-bold">{t('daf_avis_title')}</h3>
        </div>
        {!loading && (
          <button
            type="button"
            onClick={fetchReport}
            className="text-[10px] text-slate-400 hover:text-purple-300 flex items-center gap-1 transition-colors"
            data-testid="daf-refresh"
          >
            <RefreshCw className="w-3 h-3" />
            {t('daf_refresh')}
          </button>
        )}
      </div>

      {loading && (
        <div className="space-y-2 animate-pulse" data-testid="daf-loading">
          <div className="h-3 bg-purple-500/15 rounded w-full" />
          <div className="h-3 bg-purple-500/15 rounded w-11/12" />
          <div className="h-3 bg-purple-500/15 rounded w-4/5" />
          <div className="h-2" />
          <div className="h-3 bg-purple-500/15 rounded w-full" />
          <div className="h-3 bg-purple-500/15 rounded w-3/4" />
          <p className="text-[10px] text-purple-300/70 italic pt-1">{t('daf_loading_caption')}</p>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-2 rounded bg-amber-500/15 border border-amber-500/30 text-xs text-amber-100" data-testid="daf-error">
          <AlertCircle className="w-3.5 h-3.5 text-amber-300 flex-shrink-0 mt-0.5" />
          <div>
            <p>{error}</p>
            <button onClick={fetchReport} className="text-amber-300 underline mt-1 hover:text-amber-200">{t('daf_retry')}</button>
          </div>
        </div>
      )}

      {!loading && !error && report && (
        <div className="text-sm text-slate-100 leading-relaxed whitespace-pre-line" data-testid="daf-report-text">
          {report}
        </div>
      )}
    </div>
  );
}
